import { type FC, type ReactNode } from "react";
import {
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { columnStyle } from "../utils/tableColumnWidths";
import {
  columnAlignmentClass,
  visibleColumns,
  type UserManagementColumn,
} from "../utils/userManagementColumns";

/**
 * Wraps a cell in the column's permission gate, so the header cell and the
 * skeleton cell for a given column always appear and disappear together.
 */
const GatedCell: FC<{
  column: UserManagementColumn;
  children: ReactNode;
}> = ({ column, children }) =>
  column.permission ? (
    <PermissionGate allOf={[...column.permission]}>{children}</PermissionGate>
  ) : (
    <>{children}</>
  );

/** The sticky header row, sized from the column definitions. */
export const UserManagementTableHeader: FC<{
  columns: readonly UserManagementColumn[];
}> = ({ columns }) => (
  <TableHeader className="sticky top-0 z-10 bg-background">
    <TableRow>
      {visibleColumns(columns).map((column) => (
        <GatedCell key={column.key} column={column}>
          <TableHead
            className={cn(columnAlignmentClass(column.align))}
            style={columnStyle(column.width)}
          >
            {column.header}
          </TableHead>
        </GatedCell>
      ))}
    </TableRow>
  </TableHeader>
);

/**
 * Placeholder rows built from the same column list as the header, so the two
 * cannot disagree about how many cells a row has or which ones are gated.
 */
export const UserManagementSkeletonRows: FC<{
  columns: readonly UserManagementColumn[];
  rows: number;
}> = ({ columns, rows }) => (
  <>
    {Array.from({ length: rows }, (_, index) => (
      <TableRow key={index} className="h-16">
        {visibleColumns(columns).map((column) => (
          <GatedCell key={column.key} column={column}>
            <TableCell className={cn(columnAlignmentClass(column.align))}>
              {column.skeleton}
            </TableCell>
          </GatedCell>
        ))}
      </TableRow>
    ))}
  </>
);
